import { cn } from "@faire/prototype-playground/lib/utils";
import React from "react";

export interface PillButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  isPressed?: boolean;
  ariaLabel?: string;
  className?: string;
  type?: "button" | "submit" | "reset";
  variant?: "toggle" | "default"; // Add variant prop
}

/**
 * PillButton - A pill-shaped button component
 * - "toggle" variant: For filter/toggle buttons with pressed state (default)
 * - "default" variant: For regular action buttons like "Follow"
 * Based on Faire design system button patterns
 */
export default function PillButton({
  children,
  onClick,
  isPressed = false,
  ariaLabel,
  className = "",
  type = "button",
  variant = "toggle",
}: PillButtonProps) {
  // Default variant (for regular buttons like "Follow")
  if (variant === "default") {
    return (
      <button
        type={type}
        onClick={onClick}
        aria-label={ariaLabel}
        className={cn(
          "text-fs-text-primary bg-fs-surface-primary rounded-full border border-[#dfe0e1] px-6 text-sm transition-colors duration-200 ease-in-out hover:bg-(--slate-color-action-surface-inverse-hover) focus:ring-2 focus:ring-[#333333] focus:ring-offset-2 focus:outline-hidden",
          className
        )}
        style={{ height: "48px" }}
      >
        {children}
      </button>
    );
  }

  // Toggle variant (for filter buttons with pressed state)
  return (
    <button
      type={type}
      onClick={onClick}
      aria-pressed={isPressed}
      role="switch"
      aria-label={ariaLabel}
      className={cn(
        "text-fs-text-primary bg-fs-surface-primary hover:bg-fs-surface-primary flex items-center gap-2 rounded-full border border-[#dfe0e1] px-3 py-1.5 text-sm transition-colors duration-200 ease-in-out hover:border-[#333333] focus:ring-2 focus:ring-[#333333] focus:ring-offset-2 focus:outline-hidden",
        className,
        isPressed
          ? "border-[#333333] bg-[#333333] text-(--slate-color-action-text-inverse) hover:bg-[#333333]"
          : ""
      )}
    >
      {children}
    </button>
  );
}
